// Utilitários de texto de email: escape, decodificação de entidades,
// extração de texto puro de HTML e normalização de assuntos.

/** Escapa caracteres especiais de HTML. Use antes de qualquer innerHTML/dangerouslySetInnerHTML. */
export function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

/** Converte texto puro em HTML seguro, preservando quebras de linha. */
export function textToHtml(str) {
  return escapeHtml(str).replace(/\r\n|\r|\n/g, '<br>');
}

/** Decodifica entidades HTML (&nbsp;, &amp;, &ccedil;, &#231;...) sem executar nada. */
export function decodeEntities(str) {
  if (!str) return '';
  const el = document.createElement('textarea');
  el.innerHTML = String(str);
  return el.value;
}

/** Limpa o preview exibido na lista: decodifica entidades e normaliza espaços. */
export function cleanPreview(str) {
  return decodeEntities(str).replace(/\s+/g, ' ').trim();
}

/**
 * Extrai texto legível de um corpo HTML de email — remove <style>/<script>
 * (conteúdo incluso), decodifica entidades e normaliza espaços.
 * Ideal para alimentar a IA sem lixo de CSS de newsletters.
 */
export function htmlToText(html, maxLen = 6000) {
  if (!html) return '';
  let text;
  try {
    const doc = new DOMParser().parseFromString(String(html), 'text/html');
    doc.querySelectorAll('style, script, noscript, head, title').forEach(el => el.remove());
    // Quebras de linha para blocos, para o texto não virar uma linha só
    doc.querySelectorAll('br, p, div, tr, li, h1, h2, h3, h4, h5, h6, blockquote').forEach(el => {
      el.appendChild(doc.createTextNode('\n'));
    });
    text = doc.body ? doc.body.textContent || '' : '';
  } catch {
    text = String(html).replace(/<[^>]*>/g, ' ');
  }
  text = text
    .replace(/ /g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (maxLen && text.length > maxLen) return text.slice(0, maxLen) + '…';
  return text;
}

/** Prefixa "Re:" sem acumular (Re: Re: ...), aceitando RE:/re:/RES: existentes. */
export function replySubject(subject) {
  const s = (subject || '').trim();
  if (!s) return 'Re: (sem assunto)';
  return /^(re|res):/i.test(s) ? s : `Re: ${s}`;
}

/** Prefixa "Enc:" sem acumular, aceitando Enc:/Fwd:/Fw: existentes. */
export function forwardSubject(subject) {
  const s = (subject || '').trim();
  if (!s) return 'Enc: (sem assunto)';
  return /^(enc|fwd|fw):/i.test(s) ? s : `Enc: ${s}`;
}

/**
 * Decodifica um anexo base64 como texto UTF-8 (atob sozinho corrompe acentos).
 * Retorna null se o conteúdo não for decodificável.
 */
export function base64ToUtf8(base64) {
  try {
    const bin = atob(base64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return null;
  }
}
